import type { NoQService, PharmacyFilterModel } from '@develop/ia-api-library'
import { getApofinderFilters, getNoQServices, useIaQuery } from '@develop/ia-api-library'
import type { PharmacyExtended } from '~/models/pharmacy'
import type { SearchPharmacyInformationWithNoQDataQuery } from '~/models/generated/iade'
import { SearchPharmacyInformationWithNoQData as SearchPharmacyInformationWithNoQDataDocument } from '~/models/generated/iade'

type NoQServicesGql = NonNullable<NonNullable<SearchPharmacyInformationWithNoQDataQuery['getPharmacyInformationWithNoQData']>['noQ']>['noQServices']

export const useNoQPharmacyId = () => useState<number | undefined>('no-q-pharmacy-id', () => undefined)

export const usePartnersPharmacyId = () => useState<string | undefined>('partners-pharmacy-id', () => undefined)

export const useTerminPharmacy = () => useState<PharmacyExtended | undefined>('termin-pharmacy', () => undefined)

export const useServicesFilters = () => useAsyncData('apofinder:services', async () => {
  const { data: filtersData, success, message } = await getApofinderFilters()

  if (!success) {
    onShowLogs(() => console.error('Error fetching services', message))
  }
  return filtersData?.results ?? []
}, {
  getCachedData: getCachedDataWithTTL<PharmacyFilterModel[]>(30_000),
})

export const useNoqServicesFilters = () => useAsyncData('apofinder:noq-services', async () => {
  const { data: noQServicesData, success, message } = await getNoQServices()

  if (!success) {
    onShowLogs(() => console.error('Error fetching noq services', message))
  }
  return noQServicesData?.services ?? []
}, {
  getCachedData: getCachedDataWithTTL<NoQService[]>(30_000),
})

export const useNoQData = (pharmacyId: MaybeRefOrGetter<number | undefined>) => useAsyncData<SearchPharmacyInformationWithNoQDataQuery['getPharmacyInformationWithNoQData']['noQ']>(
  `apofinder:noq-data:${toValue(pharmacyId)}`,
  async () => {
    const res = await useIaQuery<SearchPharmacyInformationWithNoQDataQuery>({ query: SearchPharmacyInformationWithNoQDataDocument, variables: { pharmacyId: toValue(pharmacyId) }, cachePolicy: 'network-only' })
    return res.data.value?.getPharmacyInformationWithNoQData.noQ as SearchPharmacyInformationWithNoQDataQuery['getPharmacyInformationWithNoQData']['noQ']
  },
  {
    immediate: !!toValue(pharmacyId),
  },
)

export const useApofinderServices = async () => {
  const noQPharmacyId = useNoQPharmacyId()

  const { data: services } = await useServicesFilters()
  const { data: noqServices } = await useNoqServicesFilters()
  const { data: noQData } = useNoQData(noQPharmacyId)

  const noQServicesData = computed<NoQServicesGql>(() => {
    return noqServices.value?.map((service) => {
      const transformedService = { noqServiceFreeSpots: 0, noqServiceName: service.name, noqServiceSlug: service.slug, noqServiceUrl: '' }
      const hasSlug = noQData.value?.noQServices.map(s => s.noqServiceSlug).includes(transformedService.noqServiceSlug as string)
      if (hasSlug)
        transformedService.noqServiceFreeSpots = noQData.value?.noQServices.find(s => s.noqServiceSlug === transformedService.noqServiceSlug)?.noqServiceFreeSpots ?? 0
      return transformedService
    }) as NoQServicesGql
  })

  return {
    services,
    noqServices,
    noQServicesData,
  }
}
